// Guest Split Funnel — storage keys + the Dashboard-mount restore sequence.
// Pure orchestration over injected `api`/`storage` so it's unit-testable without
// rendering Dashboard (same pattern as lib/processingProgress.js).

// APP-1 B4 (#401): AQUI SE IMPORTABA `lib/autoSplitDismissal`, el cierre por aparato.
// Ese modulo se retira entero: la decision de reapertura la manda el servidor
// (`ventana_cerrada`, B3), que cruza de aparato y de la app a la web. Los tres sitios
// que lo consultaban quedan cubiertos sin el:
//   · el CLAIM del invitado escribe la cola, que este mismo restore relee;
//   · el paso 2 revalida la cola de `sessionStorage`, y la X ya la vacia en la pestana
//     que cerro (`dequeueAutoSplit` -> `writeAutoSplitQueue`);
//   · el paso 3 —el unico que resucitaba lo cerrado desde OTRO aparato— pregunta a
//     `GET /documents/temp-uploads/mine`, que desde B3 filtra con `$ne: True`.
// El valor que ya exista en los navegadores de la gente se ignora sin migracion.

export const AUTOSPLIT_QUEUE_KEY = 'studybuddy_auto_split_queue';
export const GUEST_CLAIM_KEY = 'studybuddy_guest_claim';
export const GUEST_FUNNEL_FLAG_KEY = 'studybuddy_from_guest_funnel';

export function readAutoSplitQueue(storage = window.sessionStorage) {
  try {
    const raw = storage.getItem(AUTOSPLIT_QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function writeAutoSplitQueue(queue, storage = window.sessionStorage) {
  // OCR-ALUMNO (remate R3): los sobres que vienen de un placeholder `por_confirmar`
  // NO se persisten: viven en el documento (la lista los vuelve a traer en cada
  // refresco) y el restaurador de esta cola pide `/documents/temp-upload/{id}`, que
  // para ellos no existe.
  const persistible = (queue || []).filter((p) => !p.documento_base_id);
  if (persistible.length === 0) {
    storage.removeItem(AUTOSPLIT_QUEUE_KEY);
  } else {
    storage.setItem(AUTOSPLIT_QUEUE_KEY, JSON.stringify(persistible));
  }
}

/**
 * Guest CTA click (F1 steps 1-2): mint the claim token via prepare-signup, persist
 * {tempUploadId, claimToken, ts} + the funnel flag in sessionStorage. The token lives
 * ONLY in sessionStorage and the POST body — never in a URL. Caller navigates after.
 */
export async function prepareGuestSignup(api, head, storage = window.sessionStorage) {
  const res = await api.post(`/documents/temp-upload/${head.temp_upload_id}/prepare-signup`);
  storage.setItem(GUEST_CLAIM_KEY, JSON.stringify({
    tempUploadId: head.temp_upload_id,
    claimToken: res.data.claim_token,
    ts: Date.now(),
  }));
  storage.setItem(GUEST_FUNNEL_FLAG_KEY, 'true');
  return res.data;
}

/**
 * Merge a restored queue into the live modal queue without clobbering it (D3 race:
 * the user starts a NEW upload while the restore is still in flight). Items already
 * in `prev` win — the visible modal head never gets yanked — and restored items the
 * user doesn't have yet are appended, deduped by temp_upload_id.
 */
export function mergeRestoredQueue(prev, restored) {
  const current = Array.isArray(prev) ? prev : [];
  const ids = new Set(current.map(p => p.temp_upload_id));
  return [...current, ...(restored || []).filter(r => !ids.has(r.temp_upload_id))];
}

/**
 * Dashboard-mount restore sequence (F2). Returns { queue, resumeMethod, expired }:
 *   1. Pending guest claim (non-guest users only): POST claim; success ensures the
 *      temp_upload is queued and clears the funnel keys; 404 with the funnel flag
 *      present → expired (F3) and clears both keys; network errors keep keys to retry.
 *   2. Generic queue revalidation — logic identical to the pre-funnel mount layer
 *      (GET each temp-upload; 404 drops silently; network errors keep the item).
 *      A 404-drop with the funnel flag still present (guest mid-funnel) → expired.
 *   3. Server fallback (non-guest only): empty queue → GET /documents/temp-uploads/mine
 *      and rehydrate each live id.
 * resumeMethod: 'claim' | 'queue' | 'server' | null — how the queue got repopulated.
 *
 * APP-1 B4 (#401) — EL CIERRE DEJÓ DE VIVIR EN EL APARATO.
 * Aquí se describía el set de descartados de la TANDA I: primero en `sessionStorage`,
 * luego en `localStorage` (`lib/autoSplitDismissal.js`), consultado en el paso 2 y en
 * el paso 3. Resolvía la reapertura DENTRO de un navegador y no fuera: un cierre hecho
 * en el teléfono no callaba a la PC, y la app y la web del mismo teléfono tampoco
 * compartían esa memoria (`capacitor://localhost` es otro origen). Ahora la decisión
 * vive en la fila del servidor (`ventana_cerrada`, B3) y los tres pasos quedan
 * cubiertos sin leer nada local: el claim escribe la cola que el paso 2 relee, la X
 * vacía esa cola en la pestaña que cerró, y el paso 3 —el único que resucitaba lo
 * cerrado desde otro aparato— pregunta a un endpoint que ya viene filtrado.
 * La reautorización explícita del regreso de Stripe tampoco desaparece: LEVANTA el
 * cierre en la fila (`cerrada: false`), así que vale desde cualquier aparato.
 */
export async function restoreAutoSplitState(api, user, storage = window.sessionStorage) {
  let resumeMethod = null;
  let lost404 = false;          // a staged temp_upload 404'd (claim or queue) during this restore
  const fromFunnel = storage.getItem(GUEST_FUNNEL_FLAG_KEY) === 'true';
  const isNonGuestUser = !!user && !user.is_guest;

  // ── 1. Guest-funnel claim ──
  let claim = null;
  try {
    claim = JSON.parse(storage.getItem(GUEST_CLAIM_KEY) || 'null');
  } catch {
    claim = null;
  }
  if (isNonGuestUser && claim && claim.claimToken) {
    try {
      const res = await api.post('/documents/temp-upload/claim', { claim_token: claim.claimToken });
      const claimedId = res.data && res.data.temp_upload_id;
      const stored = readAutoSplitQueue(storage);
      // TANDA I (B1): el claim reclama el documento igual (el token se gasta y el
      // temp_upload queda a nombre del usuario), pero si su modal se cerró con la X
      // NO se re-encola: reclamar no es pedir que la ventana vuelva a abrirse.
      if (claimedId
          && !stored.some(p => p.temp_upload_id === claimedId)) {
        stored.push({ temp_upload_id: claimedId });
        writeAutoSplitQueue(stored, storage);
      }
      storage.removeItem(GUEST_CLAIM_KEY);   // claim spent
      resumeMethod = 'claim';
    } catch (e) {
      if (e.response && e.response.status === 404) {
        // The claimed doc is gone. Note it, but DON'T decide "expired" yet — another queued
        // doc may still survive (multi-doc). The dead token is cleared either way.
        lost404 = true;
        storage.removeItem(GUEST_CLAIM_KEY);
      }
      // Anything else (network/5xx): leave the keys so a refresh retries the claim.
    }
  }

  // ── 2. Generic queue revalidation ──
  const stored = readAutoSplitQueue(storage);
  const alive = [];
  for (const item of stored) {
    /* APP-1 B4 (#401) — AQUÍ ESTABA LA PUERTA DEL CIERRE POR APARATO, y se retira.
       Filtraba los dos caminos que reencolaban lo cerrado DENTRO de este navegador: el
       claim del paso 1 (que escribe la cola y esta línea relee) y la cola que seguía
       en `sessionStorage`. Los dos siguen cubiertos sin leer nada local — la X vacía
       esta cola en la pestaña que cerró —, y el caso que de verdad dolía (cerrar en el
       teléfono y que la ventana saliera en la PC) nunca pasaba por aquí: entra por el
       paso 3, que ahora pregunta a un endpoint ya filtrado por `ventana_cerrada`. */
    try {
      const res = await api.get(`/documents/temp-upload/${item.temp_upload_id}`);
      alive.push(res.data);
    } catch (e) {
      if (e.response && e.response.status === 404) {
        lost404 = true;
        continue; // expired, drop — survival decided after all docs are checked
      }
      alive.push(item); // network error: keep, retry later
    }
  }

  // ── 3. Server fallback: sessionStorage came up empty but live staged uploads exist ──
  if (alive.length === 0 && isNonGuestUser) {
    try {
      const mineRes = await api.get('/documents/temp-uploads/mine');
      const todos = (mineRes.data && mineRes.data.temp_uploads) || [];
      // Fix 1 (anti-zombie), ahora EN EL SERVIDOR (APP-1 B4, #401): lo que el alumno
      // cerró con la X no se resucita, y ya no hace falta filtrarlo aquí ni autolimpiar
      // nada — `GET /documents/temp-uploads/mine` devuelve SÓLO lo que sigue abierto
      // (`ventana_cerrada: {$ne: true}`). Ésta era la puerta por la que un cierre hecho
      // en el teléfono no callaba a la PC: el set de descartados vivía en el navegador
      // que cerró, y este fallback corría en el otro.
      const mine = todos;
      for (const m of mine) {
        try {
          const full = await api.get(`/documents/temp-upload/${m.temp_upload_id}`);
          alive.push(full.data);
        } catch {
          // expired between list and fetch — drop
        }
      }
      if (alive.length > 0 && !resumeMethod) resumeMethod = 'server';
    } catch {
      // best-effort fallback; the queue layer already did its job
    }
  }

  writeAutoSplitQueue(alive, storage);
  if (alive.length > 0 && !resumeMethod && stored.length > 0) resumeMethod = 'queue';

  // Expired screen ONLY when the funnel user lost a staged doc AND nothing survived. If at
  // least one doc survived, show its modal and don't flag the lost ones as "expired" (the
  // multi-doc glitch where A survived but B 404'd used to wrongly trip the expired screen).
  const expired = fromFunnel && lost404 && alive.length === 0;

  // Clear the funnel flag once the journey reached a visible outcome (a restored modal or
  // the expired screen). A new CTA click re-arms it. The claim token is cleared on
  // expired too so a dead token doesn't linger.
  if (alive.length > 0 || expired) {
    storage.removeItem(GUEST_FUNNEL_FLAG_KEY);
    if (expired) storage.removeItem(GUEST_CLAIM_KEY);
  }
  return { queue: alive, resumeMethod, expired };
}
